import * as React from 'react';
import { TabsList } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

export const TAB_INDICATOR_ANIMATION_MS = 320;

type AnimatedTabsListProps = React.ComponentPropsWithoutRef<typeof TabsList> & {
  activeValue?: string;
  indicatorClassName?: string;
  /** Soft glow on the active pill using --brand */
  activeBrandShadow?: boolean;
  /** Skip the sliding pill indicator (e.g. mobile bottom nav uses underline on tabs) */
  hideIndicator?: boolean;
};

const AnimatedTabsList = React.forwardRef<
  React.ElementRef<typeof TabsList>,
  AnimatedTabsListProps
>(
  (
    {
      activeValue,
      indicatorClassName,
      activeBrandShadow,
      hideIndicator = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const suppressLayoutUpdatesRef = React.useRef(false);

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        listRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const [indicator, setIndicator] = React.useState<{
      left: number;
      width: number;
    } | null>(null);

    const updateIndicator = React.useCallback(() => {
      const list = listRef.current;
      if (!list) return;

      const active = list.querySelector<HTMLElement>(
        '[role="tab"][data-state="active"]'
      );
      if (!active) {
        setIndicator(null);
        return;
      }

      const listRect = list.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const next = {
        left: activeRect.left - listRect.left + list.scrollLeft,
        width: activeRect.width,
      };

      setIndicator(prev =>
        prev?.left === next.left && prev?.width === next.width ? prev : next
      );
    }, []);

    const updateIndicatorIfAllowed = React.useCallback(() => {
      if (suppressLayoutUpdatesRef.current) return;
      updateIndicator();
    }, [updateIndicator]);

    React.useLayoutEffect(() => {
      if (hideIndicator) return;
      updateIndicator();
      suppressLayoutUpdatesRef.current = true;

      const timer = window.setTimeout(() => {
        suppressLayoutUpdatesRef.current = false;
        updateIndicator();
      }, TAB_INDICATOR_ANIMATION_MS);

      return () => window.clearTimeout(timer);
    }, [activeValue, hideIndicator, updateIndicator]);

    React.useEffect(() => {
      if (hideIndicator) return;
      const list = listRef.current;
      if (!list) return;

      const resizeObserver = new ResizeObserver(updateIndicatorIfAllowed);
      resizeObserver.observe(list);

      const mutationObserver = new MutationObserver(updateIndicatorIfAllowed);
      mutationObserver.observe(list, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-state'],
      });

      window.addEventListener('resize', updateIndicatorIfAllowed);
      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        window.removeEventListener('resize', updateIndicatorIfAllowed);
      };
    }, [hideIndicator, updateIndicatorIfAllowed]);

    return (
      <TabsList ref={setRefs} className={cn('relative', className)} {...props}>
        {!hideIndicator && indicator && (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-y-1 rounded-full bg-card transition-[left,width] duration-300 ease-out',
              activeBrandShadow && 'shadow-card',
              indicatorClassName
            )}
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}
        {children}
      </TabsList>
    );
  }
);
AnimatedTabsList.displayName = 'AnimatedTabsList';

export { AnimatedTabsList };
